import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { HttpClient, HttpContext } from "@angular/common/http";

import { environment } from "../../../../environments/environment";
import { REFERSH } from "../../../interceptors/cache/cache.interceptor";
import { Tenant, TenantDTO, TenantSettingsDTO } from "../../../models/api/tenant";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";

@Injectable({
  providedIn: "root",
})
export class TenantAdministrationService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  /**
   * Retrieves the list of tenants.
   * @return {Observable<Tenant[]>} An observable that emits the array of tenant compact objects.
   */
  getTenants(refresh = false): Observable<Tenant[]> {
    return this.http
      .get<TenantDTO[]>(environment.baseUrlV2 + "/tenant", {
        context: new HttpContext().set(REFERSH, refresh),
      })
      .pipe(
        this.apiErrorHandler.handle("tenant"),
        map((res) => {
          if (res) {
            const tenants = res.map((tenantRes) => new Tenant(tenantRes));

            return this.order(tenants);
          } else {
            return [];
          }
        })
      );
  }

  /**
   * Retrieves tenant for given ID.
   * @param id {string} Tenant ID to request
   * @return {Observable<Tenant | null>} An observable that emits the tenant object.
   */
  getTenant(id: string): Observable<Tenant | null> {
    return this.http
      .get<TenantDTO>(environment.baseUrlV2 + "/tenant/" + id, {
        context: new HttpContext().set(REFERSH, true),
      })
      .pipe(
        this.apiErrorHandler.handle("tenant"),
        map((res) => {
          if (res) {
            return new Tenant(res);
          } else {
            return null;
          }
        })
      );
  }

  /**
   * Orders the given list of tenants by label, users by full name, cores by serial number, models by label and manufacturers by label.
   * @param tenants The list of tenants to order.
   * @returns The ordered list of tenants.
   * @private
   */
  private order(tenants: Tenant[]): Tenant[] {
    for (const tenant of tenants) {
      tenant.users = tenant.users?.sort((a, b) => {
        if (a?.firstName && a?.lastName && b?.firstName && b?.lastName) {
          const aFullName = a.firstName + " " + a.lastName;
          const bFullName = b.firstName + " " + b.lastName;
          return aFullName.localeCompare(bFullName);
        } else {
          return 0;
        }
      });
      tenant.cores = tenant.cores?.sort((a, b) => {
        if (a?.serialNumber && b?.serialNumber) {
          return a.serialNumber.localeCompare(b.serialNumber);
        } else {
          return 0;
        }
      });

      tenant.models = tenant.models?.sort((a, b) => {
        if (a?.label && b?.label) {
          return a.label.localeCompare(b.label);
        } else {
          return 0;
        }
      });

      tenant.manufacturers = tenant.manufacturers?.sort((a, b) => {
        if (a?.label && b?.label) {
          return a.label.localeCompare(b.label);
        } else {
          return 0;
        }
      });
    }

    return tenants.sort((a, b) => {
      if (a?.label && b?.label) {
        return a.label.localeCompare(b.label);
      } else {
        return 0;
      }
    });
  }

  /**
   * PUTs the given list of model IDs to the server as the new models for the tenant with the given ID.
   * @param models The list of model IDs to set for the tenant.
   * @param tenantId The ID of the tenant to set the models for.
   * @returns The response from the server.
   */
  public overrideModel(models: string[], tenantId: string) {
    const body = { models };
    return this.http
      .put(environment.baseUrlV2 + `/tenantMigrate/${tenantId}`, body)
      .pipe(this.apiErrorHandler.handle("tenant"));
  }

  /**
   * Creates a new tenant in the server.
   * @param tenant The tenant data to post to the server.
   * @returns The response from the server.
   */
  public createTenant(tenant: Tenant) {
    const tenantPost = tenant.values;
    delete tenantPost.manufacturers;
    delete tenantPost.models;
    return this.http
      .post(environment.baseUrlV2 + "/tenant", tenantPost, {
        observe: "response",
      })
      .pipe(this.apiErrorHandler.handle("tenant"));
  }
  /**
   * PUTs the given tenant object to the server.
   * @param tenant The tenant object to update on the server.
   * @returns The response from the server.
   */
  public editTenant(tenant: Tenant) {
    return this.http
      .put(environment.baseUrlV2 + `/tenant/${tenant.id}`, tenant.values, {
        observe: "response",
      })
      .pipe(this.apiErrorHandler.handle("tenant"));
  }

  /**
   * PUTs the given tenant settings to the server
   * @param tenantSettings The tenant object to update on server.
   */
  public editTenantSettings(tenantSettings: TenantSettingsDTO) {
    const { id, ...settings } = tenantSettings;
    return this.http
      .put(environment.baseUrlV2 + `/tenant/${id}/settings`, settings, { observe: "response" })
      .pipe(this.apiErrorHandler.handle("tenant"));
  }
  /**
   * Deletes a tenant from the server.
   * @param tenantId The ID of the tenant to delete.
   * @returns The response from the server.
   */
  public deleteTenant(tenantId: string | undefined) {
    return this.http
      .delete(environment.baseUrlV2 + `/tenant/${tenantId}`, {
        observe: "response",
      })
      .pipe(
        this.apiErrorHandler.handle("tenant"),
        map((res) => {
          return res.body;
        })
      );
  }
}
